import React, { useState } from 'react';
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Article as ArticleIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  VerifiedUser as VerifiedUserIcon,
  MoreHoriz as MoreIcon,
  ContactMail as ContactIcon,
  ContentPaste as ContentIcon,
  Security as SecurityIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const BottomNav = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin, isAdmin } = useAuth();
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);

  // Main navigation items (always visible)
  const mainNavItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard', show: true },
    { label: 'Blogs', icon: <ArticleIcon />, path: '/admin/blogs', show: true },
    { label: 'Profile', icon: <PersonIcon />, path: '/admin/profile', show: true },
  ];

  // Additional items in "More" menu
  const getMoreMenuItems = () => {
    const items = [
      { label: 'Content', icon: <ContentIcon />, path: '/admin/content', show: true, divider: false },
      { label: 'Contact Control', icon: <ContactIcon />, path: '/admin/contact-control', show: true, divider: false },
    ];

    // Super Admin specific items
    if (isSuperAdmin) {
      items.push(
        { label: 'User Control', icon: <PeopleIcon />, path: '/admin/users', show: true, divider: false },
        { label: 'Admin Approvals', icon: <VerifiedUserIcon />, path: '/admin/approvals', show: true, divider: true }
      );
    }

    items.push(
      { label: 'Settings', icon: <SettingsIcon />, path: '/admin/settings', show: true, divider: false }
    );

    return items.filter(item => item.show);
  };

  const moreMenuItems = getMoreMenuItems();

  // Get current value for main navigation
  const getCurrentValue = () => {
    const currentPath = location.pathname;
    const mainIndex = mainNavItems.findIndex(item => item.path === currentPath);
    if (mainIndex !== -1) return mainIndex;
    
    // Check if current path is in more menu
    const isInMoreMenu = moreMenuItems.some(item => item.path === currentPath);
    if (isInMoreMenu) return 3; // "More" button index
    
    return 0;
  };

  const handleMainNavigation = (event, newValue) => {
    if (newValue === 3) {
      // "More" button clicked
      setMoreAnchorEl(event.currentTarget);
    } else {
      navigate(mainNavItems[newValue].path);
    }
  };

  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };

  const handleMoreMenuItemClick = (item) => {
    handleMoreMenuClose();
    if (item.path) {
      navigate(item.path);
    }
  };

  // Get user role display
  const getUserRoleLabel = () => {
    if (isSuperAdmin) return 'Super Admin';
    if (isAdmin) return 'Admin';
    return 'User';
  };

  const getUserRoleIcon = () => {
    if (isSuperAdmin) return <StarIcon sx={{ fontSize: 16 }} />;
    if (isAdmin) return <SecurityIcon sx={{ fontSize: 16 }} />;
    return null;
  };

  return (
    <>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: { xs: 'flex', lg: 'none' },
          borderRadius: 0,
          backgroundColor: 'background.paper',
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
        elevation={3}
      >
        <BottomNavigation
          value={getCurrentValue()}
          onChange={handleMainNavigation}
          sx={{
            height: 65,
            width: '100%',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 12px',
            },
            '& .Mui-selected': {
              color: theme.palette.primary.main,
            },
          }}
        >
          {mainNavItems.map((item, index) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={item.icon}
              value={index}
              showLabel={true}
              sx={{
                '&.Mui-selected': {
                  '& .MuiBottomNavigationAction-label': {
                    fontSize: '0.75rem',
                  },
                },
              }}
            />
          ))}
          
          {/* More Button */}
          <BottomNavigationAction
            label="More"
            icon={<MoreIcon />}
            value={3}
            showLabel={true}
            sx={{
              '&.Mui-selected': {
                color: theme.palette.primary.main,
              },
            }}
          />
        </BottomNavigation>
      </Paper>

      {/* More Menu */}
      <Menu
        anchorEl={moreAnchorEl}
        open={Boolean(moreAnchorEl)}
        onClose={handleMoreMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            bottom: 65,
            position: 'absolute',
            borderRadius: 2,
            minWidth: 200,
            maxWidth: '90vw',
            boxShadow: theme.shadows[5],
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {/* User Role Header */}
        <MenuItem disabled sx={{ opacity: 1, bgcolor: theme.palette.grey[50] }}>
          <ListItemIcon>
            {getUserRoleIcon()}
          </ListItemIcon>
          <ListItemText 
            primary={getUserRoleLabel()}
            secondary={isSuperAdmin ? "Full system access" : "Limited access"}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </MenuItem>
        
        <Divider />

        {/* Menu Items */}
        {moreMenuItems.map((item, index) => (
          <React.Fragment key={item.label}>
            <MenuItem
              onClick={() => handleMoreMenuItemClick(item)}
              sx={{
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{ 
                  variant: 'body2',
                  fontWeight: 400
                }}
              />
            </MenuItem>
            {item.divider && index < moreMenuItems.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Menu>
    </>
  );
};

export default BottomNav;